import dayjs, {Dayjs} from 'dayjs';
import {amber, blueGrey, deepPurple, green, indigo, teal} from '@mui/material/colors';

import {PlannerPersonDto, PlannerViewDto, TeamsPresenceDto} from 'apiClient/Api';

import {CalendarEventDetail} from './calendarSources';

/**
 * Zoomstufe der Tafel.
 *
 * Die Stufe bestimmt zweierlei: welchen Zeitraum die Tafel zeigt **und** wie breit eine Stunde
 * gezeichnet wird. Beides gehört zusammen — ein Monat mit der Auflösung eines Tages wäre meterweit,
 * ein Tag mit der Auflösung eines Monats leer.
 */
export type PlannerRange = 'day' | 'week' | 'twoWeeks' | 'month';

/**
 * Breite einer Stunde in Pixeln je Zoomstufe.
 *
 * **Warum Pixel und nicht Prozent:** vorher füllte die Tafel immer genau die Fensterbreite. In der
 * Woche blieben für eine Stunde gut ein Prozent — in einen so schmalen Balken passt kein Wort, und
 * die Zeile wurde zu Konfetti. Mit fester Stundenbreite ist ein Ein-Stunden-Termin immer gleich
 * breit; wird die Woche dadurch breiter als das Fenster, scrollt die Tafel waagerecht. Genau so
 * arbeiten Planungstafeln.
 */
export const PIXELS_PER_HOUR: Record<PlannerRange, number> = {
    day: 96,
    week: 30,
    twoWeeks: 16,
    month: 8,
};

/** Ab welcher Balkenbreite (Pixel) der Titel hineingeschrieben wird. */
export const LABEL_MIN_PX = 46;

/**
 * Grenzen des stufenlosen Zooms, als Vielfaches der eingepassten Breite.
 *
 * Nach unten bis auf ein Viertel: damit passt auch ein Monat mit Wochenenden noch ins Fenster.
 * Nach oben das Achtfache — darüber ist eine Stunde breiter als der Bildschirm, und die Tafel
 * beantwortet keine Frage mehr, die man an eine Tafel stellt.
 */
export const ZOOM_MIN = 0.25;

export const ZOOM_MAX = 8;

/**
 * Ein Zoomschritt (Knopfdruck oder Mausradraste).
 *
 * 1,2 ist der Wert, der sich flüssig anfühlt: klein genug, um eine Zwischengröße zu treffen, groß
 * genug, um mit wenigen Rasten von einer Woche auf einen Vormittag zu kommen.
 */
export const ZOOM_STEP = 1.2;

/** Härteste Grenzen der Stundenbreite — unabhängig von Zoomstufe und Fensterbreite. */
export const MIN_PIXELS_PER_HOUR = 3;

export const MAX_PIXELS_PER_HOUR = 600;

/**
 * Der gezeigte Tagesabschnitt. Nur Arbeitszeit statt volle 24 Stunden — in der Wochenansicht
 * schrumpft eine Stunde sonst auf ein halbes Prozent der Breite und ist als Balken nicht mehr
 * erkennbar. Termine außerhalb werden auf den Rand geklemmt, damit sie nicht verschwinden.
 */
export const DAY_START_HOUR = 7;
export const DAY_END_HOUR = 19;

/** Minuten, die ein Tag im Zeitstrahl beiträgt. */
const MINUTES_PER_DAY = (DAY_END_HOUR - DAY_START_HOUR) * 60;

/**
 * Das Band, in dem ein Balken liegt.
 *
 * Ganztägiges kommt in ein eigenes Band über die Zeitbalken — es hat keine Uhrzeit, und im
 * Zeitraster gezeichnet würde es die Termine verdecken, die tatsächlich eine haben.
 */
export type PlannerLane = 'allDay' | 'timed';

/** Die Kategorien, nach denen eingefärbt wird — dieselbe Aufteilung wie im Kalender. */
export type PlannerCategory =
    | 'order' | 'appointment' | 'absence' | 'project' | 'task' | 'other' | 'outlook' | 'outlookFree';

/**
 * Farbe je Kategorie.
 *
 * Bewusst feste Werte statt der Theme-Rollen: `primary` und `info` liegen in diesem Theme beide im
 * Türkis-Bereich, wodurch Labelwin und Outlook auf der Tafel gleich aussahen — und genau die
 * Unterscheidung ist der Zweck. Urlaub bekommt Bernstein, weil Abwesenheit die Aussage ist, die
 * man auf einer Planungstafel zuerst sucht.
 */
export const CATEGORY_COLOR: Record<PlannerCategory, string> = {
    order: teal[600],
    appointment: green[600],
    absence: amber[700],
    project: indigo[500],
    task: blueGrey[400],
    other: blueGrey[300],
    outlook: deepPurple[400],
    outlookFree: deepPurple[100],
};

/** Ein Balken auf der Tafel. */
export interface PlannerBar {
    key: string;
    /** Linke Kante in Pixeln. */
    left: number;
    /** Breite in Pixeln. */
    width: number;
    title: string;
    /** Kurztext für den Mauszeiger — Zeitraum und Herkunft. */
    tooltip: string;
    lane: PlannerLane;
    /**
     * Etage innerhalb des Zeitbands. Nur belegt, wenn sich Balken tatsächlich überlappen —
     * feste Spuren je Quelle hätten aus jeder Zeile drei dünne Streifen gemacht, auch wenn nichts
     * kollidiert. So bleibt eine Zeile flach, solange nichts gleichzeitig läuft.
     */
    stack: number;
    category: PlannerCategory;
    color: string;
    /** „Frei" markiert (Outlook) — blockiert keine Zeit, wird gestrichelt gezeichnet. */
    free: boolean;
    /**
     * Online-Besprechung (in der Regel Teams). Der Balken trägt dann ein Kamerasymbol: ob ein
     * Termin am Schreibtisch stattfindet oder eine Anfahrt bedeutet, ändert die Planung.
     */
    online: boolean;
    /** Rohdaten für den Detail-Dialog. */
    detail: CalendarEventDetail;
}

/**
 * Der Zustand einer Person **jetzt** — die Frage, die man auf einer Planungstafel zuerst stellt.
 *
 * `unknown` heißt „heute ist nicht im Blick"; dann wird gar kein Zustand gezeigt, statt einen zu
 * behaupten, der sich auf einen anderen Tag bezieht.
 */
export type PlannerStatus = 'free' | 'busy' | 'absent' | 'unknown';

/**
 * Der Teams-Status, auf die fünf Zustände zusammengezogen, die eine Farbe verdienen.
 *
 * Microsoft kennt feinere Abstufungen (`availableIdle`, `busyIdle`, `beRightBack`); für die Frage
 * „kann ich die Person jetzt ansprechen?" bedeuten sie dasselbe wie ihr Grundzustand. Die
 * Feinheit bleibt trotzdem sichtbar — sie steht im Text, nur nicht in der Farbe.
 */
export type PresenceState = 'available' | 'busy' | 'doNotDisturb' | 'away' | 'offline';

/** Der Teams-Status einer Zeile, aufbereitet für die Anzeige. */
export interface PlannerPresence {
    state: PresenceState;
    /** Übersetzungsschlüssel des Klartexts — übersetzt wird in der Anzeige, nicht im Modell. */
    key: string;
    /** Gerade in einem Gespräch oder einer Besprechung — verdient ein eigenes Zeichen. */
    inCall: boolean;
}

/** Eine Zeile der Tafel. */
export interface PlannerRow {
    identifier: string;
    label: string;
    /** Klartext-Hinweise zu fehlenden Quellen — erscheinen als Warnzeichen an der Zeile. */
    notes: string[];
    bars: PlannerBar[];
    /** Hat die Zeile ganztägige Einträge? Nur dann wird das Band gezeichnet. */
    hasAllDay: boolean;
    /** Wie viele Etagen das Zeitband braucht — bestimmt die Höhe der Zeile. */
    stacks: number;
    /** Zustand jetzt. */
    status: PlannerStatus;
    /**
     * Bis wann der Zustand gilt (`HH:mm`): bei „belegt" das Ende des laufenden Termins, bei
     * „frei" der Beginn des nächsten. Ohne Angabe, wenn heute nichts mehr ansteht.
     */
    statusUntil?: string;
    /**
     * Der Teams-Status, sofern Microsoft ihn liefert.
     *
     * Er ersetzt den aus dem Kalender gerechneten Zustand nicht, sondern steht daneben: der
     * Kalender sagt, was **geplant** war, die Präsenz, was **gerade** ist. Wer keinen Termin hat
     * und trotzdem telefoniert, ist im Kalender frei und hier im Gespräch — und genau diese
     * Auskunft ist die nützlichere, wenn jemand fragt „kann ich kurz?".
     */
    presence?: PlannerPresence;
}

/**
 * Die Tage, die die Tafel zeigt.
 *
 * Ohne Wochenende fallen Samstag und Sonntag heraus — sie fraßen knapp ein Drittel der Breite für
 * Spalten, in denen nie etwas steht. Wer am Wochenende plant, schaltet sie zu.
 *
 * **Woche und zwei Wochen sind ein rollendes Fenster ab dem gewählten Tag**, keine feste
 * Kalenderwoche. An einem Dienstag sind Montag und alles davor abgearbeitet; Spalten dafür kosten
 * Platz und zeigen nichts, was noch zu planen wäre. Das Fenster reicht stattdessen in die kommende
 * Woche hinein — am Dienstag also bis zum nächsten Montag. Angenehmer Nebeneffekt: ohne Wochenende
 * bleiben es **immer** fünf beziehungsweise zehn Arbeitstage, egal an welchem Wochentag man steht,
 * und damit bleibt die Spaltenbreite über die Woche hinweg gleich.
 */
export const plannerDays = (
    anchor: Dayjs,
    range: PlannerRange,
    includeWeekends: boolean,
): Dayjs[] => {
    const all: Dayjs[] = [];

    if (range === 'day') {
        all.push(anchor.startOf('day'));
    } else if (range === 'month') {
        const first = anchor.startOf('month');
        const length = anchor.daysInMonth();
        for (let i = 0; i < length; i += 1) all.push(first.add(i, 'day'));
    } else {
        const first = anchor.startOf('day');
        const length = range === 'twoWeeks' ? 14 : 7;
        for (let i = 0; i < length; i += 1) all.push(first.add(i, 'day'));
    }

    // Beim Tagesblick bleibt der gewählte Tag stehen, auch wenn er auf ein Wochenende fällt —
    // sonst zeigt die Tafel einen anderen Tag als den, auf den man geklickt hat.
    if (includeWeekends || range === 'day') return all;
    return all.filter((day) => day.day() !== 0 && day.day() !== 6);
};

/** Zeitpunkt ohne Zonenangabe — der Server liefert Wanduhrzeit. */
const wallClock = (value: string): string => value.replace(/(Z|[+-]\d{2}:\d{2})$/, '');

/** Position eines Zeitpunkts als Tag und Minute im gezeigten Abschnitt. */
const locate = (value: string, days: Dayjs[]): {day: number; minute: number} | null => {
    const at = dayjs(wallClock(value));
    const day = days.findIndex((d) => d.isSame(at, 'day'));
    if (day === -1) return null;

    const minuteOfDay = at.hour() * 60 + at.minute();
    // Auf den gezeigten Abschnitt klemmen: ein Termin um 6:30 rutscht an den linken Rand, statt
    // aus der Zeile zu fallen.
    const minute = Math.min(Math.max(minuteOfDay - DAY_START_HOUR * 60, 0), MINUTES_PER_DAY);
    return {day, minute};
};

/** Kleinste sichtbare Balkenbreite in Pixeln — sonst verschwinden kurze Termine ganz. */
const MIN_BAR_WIDTH = 6;

/** Ein Eintrag, bevor daraus Geometrie wird. */
interface RawItem {
    key: string;
    title: string;
    tooltip: string;
    category: PlannerCategory;
    lane: PlannerLane;
    free: boolean;
    online: boolean;
    detail: CalendarEventDetail;
    /** Erster gezeigter Tag des Eintrags. */
    fromDay: number;
    /** Letzter gezeigter Tag (bei ganztägig identisch mit `fromDay`, vor dem Zusammenfassen). */
    toDay: number;
    /** Minute im Tag; bei ganztägig ohne Bedeutung. */
    fromMinute: number;
    toMinute: number;
}

/** Kategorie eines Labelwin-Eintrags. */
const categoryOf = (kind?: string | null): PlannerCategory => {
    switch (kind) {
        case 'Order':
            return 'order';
        case 'Appointment':
            return 'appointment';
        case 'Absence':
            return 'absence';
        case 'Project':
            return 'project';
        case 'Task':
            return 'task';
        default:
            return 'other';
    }
};

/**
 * Fasst gleichnamige ganztägige Einträge über aufeinanderfolgende Tage zu einem Balken zusammen.
 *
 * Labelwin führt einen Urlaub als **einen Satz je Tag**. Ohne dieses Zusammenfassen stünde auf der
 * Tafel fünfmal derselbe Text „Urlaub vom 31.07. bis …" nebeneinander — genau das Bild, das eine
 * Planungstafel unlesbar macht. Zusammengefasst wird nur, was denselben Titel trägt und lückenlos
 * anschließt; eine Lücke bleibt eine Lücke.
 */
const mergeAllDay = (items: RawItem[]): RawItem[] => {
    const allDay = items.filter((i) => i.lane === 'allDay');
    const rest = items.filter((i) => i.lane !== 'allDay');
    const merged: RawItem[] = [];

    const byTitle = new Map<string, RawItem[]>();
    allDay.forEach((item) => {
        const list = byTitle.get(item.title) ?? [];
        list.push(item);
        byTitle.set(item.title, list);
    });

    byTitle.forEach((list) => {
        const sorted = [...list].sort((a, b) => a.fromDay - b.fromDay);
        let current = sorted[0];

        for (let i = 1; i < sorted.length; i += 1) {
            const next = sorted[i];
            if (next.fromDay <= current.toDay + 1) {
                current = {...current, toDay: Math.max(current.toDay, next.toDay)};
            } else {
                merged.push(current);
                current = next;
            }
        }

        merged.push(current);
    });

    return [...merged, ...rest];
};

/** Rechnet einen Eintrag in einen Balken um. */
const toBar = (item: RawItem, days: Dayjs[], pixelsPerHour: number): PlannerBar => {
    const perMinute = pixelsPerHour / 60;
    const total = days.length * MINUTES_PER_DAY * perMinute;

    const from = (item.lane === 'allDay'
        ? item.fromDay * MINUTES_PER_DAY
        : item.fromDay * MINUTES_PER_DAY + item.fromMinute) * perMinute;

    const to = (item.lane === 'allDay'
        ? (item.toDay + 1) * MINUTES_PER_DAY
        : item.toDay * MINUTES_PER_DAY + Math.max(item.toMinute, item.fromMinute + 15)) * perMinute;

    const left = from;
    const width = Math.max(to - from, MIN_BAR_WIDTH);

    return {
        key: item.key,
        left,
        width: Math.min(width, total - left),
        title: item.title,
        tooltip: item.tooltip,
        lane: item.lane,
        stack: 0,
        category: item.category,
        color: CATEGORY_COLOR[item.category],
        free: item.free,
        online: item.online,
        detail: item.detail,
    };
};

/**
 * Bringt die Antwort des Planers auf Zeilen und Balken.
 *
 * Eine Person ohne jede Quelle bleibt als leere Zeile stehen — sie würde sonst unbemerkt aus der
 * Planung fallen, und genau das wäre der teure Fehler.
 */
export const toPlannerRows = (
    view: PlannerViewDto | undefined,
    days: Dayjs[],
    pixelsPerHour: number,
    label: (key: string) => string,
    formatPeriod: (start?: string | null, end?: string | null, allDay?: boolean) => string,
): PlannerRow[] =>
    (view?.people ?? []).map((person) => buildRow(person, days, pixelsPerHour, label, formatPeriod));

const buildRow = (
    person: PlannerPersonDto,
    days: Dayjs[],
    pixelsPerHour: number,
    label: (key: string) => string,
    formatPeriod: (start?: string | null, end?: string | null, allDay?: boolean) => string,
): PlannerRow => {
    const items: RawItem[] = [];
    const notes: string[] = [];

    if (!person.labelwinAvailable && person.labelwinMessage) notes.push(person.labelwinMessage);
    if (!person.outlookAvailable && person.outlookMessage) notes.push(person.outlookMessage);

    const push = (
        key: string,
        start: string | null | undefined,
        end: string | null | undefined,
        title: string,
        category: PlannerCategory,
        allDay: boolean,
        free: boolean,
        online: boolean,
        detail: CalendarEventDetail,
    ): void => {
        if (!start) return;
        const from = locate(start, days);
        if (!from) return;

        const to = end ? locate(end, days) : null;

        items.push({
            key,
            title,
            tooltip: `${title} · ${formatPeriod(start, end, allDay)}`,
            category,
            lane: allDay ? 'allDay' : 'timed',
            free,
            online,
            detail,
            fromDay: from.day,
            // Ein Termin über Mitternacht endet auf der Tafel am Anfangstag — Labelwin kennt
            // solche Sätze nicht, und ein Balken über die Tagesgrenze wäre hier irreführend.
            toDay: to && to.day >= from.day ? to.day : from.day,
            fromMinute: from.minute,
            toMinute: to ? to.minute : from.minute + 30,
        });
    };

    for (const entry of person.entries ?? []) {
        const allDay = (entry.isAllDay ?? false) || !entry.start || !entry.end;
        push(`e${entry.id}`, entry.start ?? `${entry.date}T00:00:00`, entry.end,
            entry.subject?.trim() || '—', categoryOf(entry.kind), allDay, false, false,
            {kind: 'entry', entry});
    }

    for (const task of person.tasks ?? []) {
        push(`t${task.id}`, task.start ?? `${task.date}T00:00:00`, task.end,
            task.subject?.trim() || '—', 'task', !task.start, false, false, {kind: 'task', task});
    }

    for (const event of person.outlookEntries ?? []) {
        if (event.isCancelled) continue;
        const free = event.showAs === 'free';
        push(`o${event.id}`, event.start, event.end,
            event.subject?.trim() || label('calendar.noSubject'),
            free ? 'outlookFree' : 'outlook', event.isAllDay ?? false, free,
            event.isOnlineMeeting ?? false, {kind: 'outlook', event});
    }

    const merged = mergeAllDay(items);
    const bars = merged.map((item) => toBar(item, days, pixelsPerHour));
    const stacks = packTimedBars(bars);
    const {status, statusUntil} = currentStatus(person, days);

    return {
        identifier: person.identifier ?? '',
        label: person.name?.trim() || person.identifier || '—',
        notes,
        bars,
        hasAllDay: bars.some((bar) => bar.lane === 'allDay'),
        stacks,
        status,
        statusUntil,
        presence: toPresence(person.presence),
    };
};

/**
 * Übersetzt den Teams-Status in Zustand, Text und das Merkmal „im Gespräch".
 *
 * `presenceUnknown` und ein leerer Wert führen bewusst zu **keiner** Anzeige: „unbekannt" ist
 * keine Auskunft, sondern deren Fehlen — dann soll die Zeile den Kalenderzustand zeigen, statt
 * einen Teams-Status zu behaupten, den niemand gemeldet hat.
 */
const toPresence = (presence?: TeamsPresenceDto | null): PlannerPresence | undefined => {
    const availability = presence?.availability?.toLowerCase();
    if (!availability || availability === 'presenceunknown') return undefined;

    const activity = presence?.activity?.toLowerCase();
    const inCall = activity === 'inacall' || activity === 'inaconferencecall' || activity === 'inameeting';

    // Die Tätigkeit ist die genauere Auskunft — „im Gespräch" sagt mehr als „beschäftigt". Sie gilt
    // aber nur, solange der Grundzustand sie stützt: wer auf „abwesend" steht, ist nicht im
    // Gespräch, auch wenn Teams die letzte Tätigkeit noch mitschickt.
    const activityKey = activity === 'inacall' || activity === 'inaconferencecall'
        ? 'planner.presenceInCall'
        : activity === 'inameeting'
            ? 'planner.presenceInMeeting'
            : activity === 'presenting'
                ? 'planner.presencePresenting'
                : undefined;

    switch (availability) {
        case 'available':
        case 'availableidle':
            return {state: 'available', key: 'planner.presenceAvailable', inCall: false};
        case 'busy':
        case 'busyidle':
            return {state: 'busy', key: activityKey ?? 'planner.presenceBusy', inCall};
        case 'donotdisturb':
            return {state: 'doNotDisturb', key: activityKey ?? 'planner.presenceDoNotDisturb', inCall};
        case 'away':
            return {state: 'away', key: 'planner.presenceAway', inCall: false};
        case 'berightback':
            return {state: 'away', key: 'planner.presenceBeRightBack', inCall: false};
        case 'offline':
            return {state: 'offline', key: 'planner.presenceOffline', inCall: false};
        default:
            // Ein Zustand, den Microsoft nach diesem Stand ergänzt hat: lieber neutral zeigen als
            // verschweigen — dass jemand erreichbar ist, wäre die gefährlichere Annahme.
            return {state: 'offline', key: 'planner.presenceOffline', inCall};
    }
};

/**
 * Ermittelt, ob eine Person gerade frei, belegt oder abwesend ist.
 *
 * Gerechnet wird auf den **echten Zeiten** der Quelle, nicht auf der Geometrie der Tafel: die
 * kennt nur Prozente und ist auf den gezeigten Tagesabschnitt geklemmt. Ein Termin um 6:30 würde
 * dort am Rand kleben und den Zustand um 6:45 falsch beantworten.
 *
 * „Frei" markierte Outlook-Termine zählen nicht als belegt — sie blockieren keine Zeit.
 */
const currentStatus = (
    person: PlannerPersonDto,
    days: Dayjs[],
): {status: PlannerStatus; statusUntil?: string} => {
    const now = dayjs();
    if (!days.some((d) => d.isSame(now, 'day'))) return {status: 'unknown'};

    const covers = (start?: string | null, end?: string | null): boolean => {
        if (!start || !end) return false;
        const from = dayjs(wallClock(start));
        const to = dayjs(wallClock(end));
        return !now.isBefore(from) && now.isBefore(to);
    };

    // Abwesenheit schlägt alles: wer im Urlaub ist, ist auch dann nicht verfügbar, wenn im
    // Kalender zufällig gerade nichts steht.
    const absent = (person.entries ?? []).some((entry) =>
        entry.kind === 'Absence' && entry.date && dayjs(entry.date).isSame(now, 'day'));
    if (absent) return {status: 'absent'};

    const running = (person.entries ?? []).find((entry) => covers(entry.start, entry.end))
        ?? (person.outlookEntries ?? []).find((event) =>
            !event.isCancelled && event.showAs !== 'free' && covers(event.start, event.end));

    if (running) {
        const end = 'end' in running ? running.end : undefined;
        return {status: 'busy', statusUntil: end ? dayjs(wallClock(end)).format('HH:mm') : undefined};
    }

    // Frei — bis zum nächsten Termin des Tages.
    const upcoming = [
        ...(person.entries ?? []).map((e) => e.start),
        ...(person.outlookEntries ?? [])
            .filter((e) => !e.isCancelled && e.showAs !== 'free')
            .map((e) => e.start),
    ]
        .filter((value): value is string => !!value)
        .map((value) => dayjs(wallClock(value)))
        .filter((at) => at.isSame(now, 'day') && at.isAfter(now))
        .sort((a, b) => a.valueOf() - b.valueOf())[0];

    return {status: 'free', statusUntil: upcoming?.format('HH:mm')};
};

/**
 * Verteilt sich überlappende Zeitbalken auf Etagen und gibt deren Anzahl zurück.
 *
 * Bewusst erst bei echter Überlappung: eine Zeile, in der nichts kollidiert, bleibt einetagig und
 * damit flach. Feste Spuren je Quelle hätten jede Zeile auf drei dünne Streifen aufgebläht — das
 * ist die Optik, die eine Tafel billig aussehen lässt, und sie sagt nichts, solange sich nichts
 * überschneidet.
 */
const packTimedBars = (bars: PlannerBar[]): number => {
    const timed = bars
        .filter((bar) => bar.lane === 'timed')
        .sort((a, b) => a.left - b.left);

    // Je Etage die rechte Kante des zuletzt gelegten Balkens.
    const ends: number[] = [];

    for (const bar of timed) {
        // Ein Pixel Toleranz, damit direkt aneinander grenzende Termine nicht als Überlappung gelten.
        let floor = ends.findIndex((end) => end <= bar.left + 1);
        if (floor === -1) {
            floor = ends.length;
            ends.push(0);
        }
        ends[floor] = bar.left + bar.width;
        bar.stack = floor;
    }

    return Math.max(1, ends.length);
};

/**
 * Ein Eintrag einer Person an einem Tag — ohne jede Geometrie.
 *
 * Das Gegenstück zu <see cref="PlannerBar" />: dieselbe Herkunft, dieselben Rohdaten, aber statt
 * Pixeln nur Zeit und Text. Auf schmalen Geräten wird aus der Zeitachse eine Liste, und eine Liste
 * braucht keine Balkenbreiten.
 */
export interface PlannerAgendaItem {
    key: string;
    title: string;
    category: PlannerCategory;
    /** Ganztägig — Urlaub, Krank, Wiedervorlagen ohne Uhrzeit. */
    allDay: boolean;
    /** Wanduhrzeit `HH:mm`, oder `undefined` bei ganztägigen Einträgen. */
    from?: string;
    to?: string;
    /** Online-Besprechung — verdient in der Liste ein Kamerasymbol. */
    online: boolean;
    /** Als „frei" markiert: belegt keine Zeit, wird blasser gezeigt. */
    free: boolean;
    detail: CalendarEventDetail;
}

/** Eine Person mit ihrem Zustand und dem, was an einem Tag ansteht. */
export interface PlannerAgendaPerson {
    identifier: string;
    label: string;
    notes: string[];
    status: PlannerStatus;
    statusUntil?: string;
    presence?: PlannerPresence;
    items: PlannerAgendaItem[];
}

/**
 * Bringt die Antwort des Planers auf eine Liste je Person für **einen** Tag.
 *
 * **Warum das neben <see cref="toPlannerRows" /> steht:** Eine Zeitachse beantwortet „wann liegt
 * was nebeneinander" — dafür braucht sie Breite. Auf einem Telefon bleiben je Tag rund dreißig
 * Punkte, und darin ist kein Balken mehr lesbar. Die Frage, für die man die Tafel aufmacht, lässt
 * sich aber auch ohne Achse beantworten: wer ist gerade frei, und was steht an. Genau das liefert
 * diese Form — dieselben Daten, andere Darstellung.
 */
export const toPlannerAgenda = (
    view: PlannerViewDto | undefined,
    day: Dayjs,
    label: (key: string) => string,
): PlannerAgendaPerson[] =>
    (view?.people ?? []).map((person) => {
        const items: PlannerAgendaItem[] = [];
        const notes: string[] = [];

        if (!person.labelwinAvailable && person.labelwinMessage) notes.push(person.labelwinMessage);
        if (!person.outlookAvailable && person.outlookMessage) notes.push(person.outlookMessage);

        const push = (
            key: string,
            start: string | null | undefined,
            end: string | null | undefined,
            title: string,
            category: PlannerCategory,
            allDay: boolean,
            free: boolean,
            online: boolean,
            detail: CalendarEventDetail,
        ): void => {
            if (!start) return;

            const from = dayjs(wallClock(start));
            const to = end ? dayjs(wallClock(end)) : null;

            // Ein mehrtägiger Eintrag gehört an jeden Tag, den er berührt — sonst fehlt der Urlaub
            // an genau dem Tag, an dem man nachsieht.
            const touchesDay = from.isSame(day, 'day')
                || (to !== null && !from.isAfter(day, 'day') && !to.isBefore(day, 'day'));
            if (!touchesDay) return;

            items.push({
                key,
                title,
                category,
                allDay,
                from: allDay ? undefined : from.format('HH:mm'),
                to: allDay || !to ? undefined : to.format('HH:mm'),
                online,
                free,
                detail,
            });
        };

        for (const entry of person.entries ?? []) {
            const allDay = (entry.isAllDay ?? false) || !entry.start || !entry.end;
            push(`e${entry.id}`, entry.start ?? `${entry.date}T00:00:00`, entry.end,
                entry.subject?.trim() || '—', categoryOf(entry.kind), allDay, false, false,
                {kind: 'entry', entry});
        }

        for (const task of person.tasks ?? []) {
            push(`t${task.id}`, task.start ?? `${task.date}T00:00:00`, task.end,
                task.subject?.trim() || '—', 'task', !task.start, false, false,
                {kind: 'task', task});
        }

        for (const event of person.outlookEntries ?? []) {
            if (event.isCancelled) continue;
            const free = event.showAs === 'free';
            push(`o${event.id}`, event.start, event.end,
                event.subject?.trim() || label('calendar.noSubject'),
                free ? 'outlookFree' : 'outlook', event.isAllDay ?? false, free,
                event.isOnlineMeeting ?? false, {kind: 'outlook', event});
        }

        // Ganztägiges zuerst — es rahmt den Tag, statt in ihm zu liegen.
        items.sort((a, b) => {
            if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
            return (a.from ?? '').localeCompare(b.from ?? '');
        });

        const {status, statusUntil} = currentStatus(person, [day]);

        return {
            identifier: person.identifier ?? '',
            label: person.name?.trim() || person.identifier || '—',
            notes,
            status,
            statusUntil,
            presence: toPresence(person.presence),
            items,
        };
    });
